import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  listenToShortGuestReservations,
  rescheduleShortstayReservation,
} from "../../../../redux/shortstayReservationSlice";

const GuestRescheduleShortstay: React.FC = () => {
  const {
    guestId,
    listingId,
    reservationId,
    checkIn,
    checkOut,
  } = useParams<{
    guestId: string;
    listingId: string;
    reservationId: string;
    checkIn: string;
    checkOut: string;
  }>();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const reservations = useSelector(
    (state: RootState) => state.shortstayReservations.guestReservations
  );
  const booking = reservations.find(res => res.id === reservationId);

  const [modalOpen, setModalOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState<null | {
    reservedDates: number;
    pricePerNight: number;
    extraCharge: number;
    totalCharge: number;
    chargeRequired: boolean;
  }>(null);

  useEffect(() => {
    if (guestId) {
      dispatch(listenToShortGuestReservations(guestId));
    }
  }, [dispatch, guestId]);

  const handleConfirm = async () => {
    if (!booking?.id || !checkIn || !checkOut) return;
    setConfirming(true);
  
    const result = await dispatch(
      rescheduleShortstayReservation({
        reservationId: booking.id,
        newCheckIn: checkIn,
        newCheckOut: checkOut,
      })
    );
  
    if (rescheduleShortstayReservation.fulfilled.match(result)) {
      const {
        reservedDates,
        extraCharge,
        totalCharge,
        chargeRequired,
      } = result.payload;
  
      const pricePerNight = booking.totalPaid / booking.reservedDates;
  
      setPreview({
        reservedDates,
        extraCharge,
        totalCharge,
        chargeRequired,
        pricePerNight,
      });
  
      setTimeout(() => {
        if (chargeRequired) {
          navigate(`/ConfirmGuestRescheduleShortstay/${guestId}/${listingId}/${reservationId}`);
        } else {
          navigate(`/UpdateGuestRescheduleShortstay/${guestId}/${listingId}/${reservationId}`);
        }
      }, 1200);
    } else {
      alert("Failed to reschedule reservation.");
    }
  
    setConfirming(false);
  };
  

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("default", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div style={styles.page}>
      <h2 style={styles.title}>Reschedule Reservation</h2>

      <div style={styles.card}>
        <h3 style={styles.subTitle}>Your Current Booking</h3>
        <p><strong>Check-In:</strong> {formatDate(booking?.checkIn)}</p>
        <p><strong>Check-Out:</strong> {formatDate(booking?.checkOut)}</p>
        <p><strong>Status:</strong> {booking?.reservationStatus}</p>

        <hr style={styles.hr} />

        <h3 style={styles.subTitle}>New Booking Dates</h3>
        <p><strong>New Check-In:</strong> {formatDate(checkIn)}</p>
        <p><strong>New Check-Out:</strong> {formatDate(checkOut)}</p>

        <p style={{ color: "#ccc", marginTop: 10 }}>
          Your total cost will be recalculated based on the updated stay.
        </p>

        {preview && (
          <div style={{ marginTop: 20 }}>
            <h3 style={styles.subTitle}>Reschedule Summary</h3>
            <p><strong>Total Nights:</strong> {preview.reservedDates}</p>
            <p><strong>Price Per Night:</strong> ${preview.pricePerNight}</p>
            <p><strong>Extra Charge:</strong> ${preview.extraCharge}</p>
            <p><strong>New Total:</strong> ${preview.totalCharge}</p>
            <p style={{ color: preview.chargeRequired ? "#f0c040" : "#0f0" }}>
              {preview.chargeRequired ? "💳 Additional payment required." : "✅ No extra payment needed."}
            </p>
          </div>
        )}

        <button onClick={() => setModalOpen(true)} style={styles.confirmBtn}>
          Confirm Change
        </button>
      </div>

      {modalOpen && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h4>Confirm Reschedule?</h4>
            <p>This will update your booking dates and redirect accordingly.</p>
            <button
              onClick={handleConfirm}
              disabled={confirming}
              style={styles.modalBtn}
            >
              {confirming ? "Updating..." : "Yes, Confirm"}
            </button>
            <button onClick={() => setModalOpen(false)} style={styles.cancelBtn}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestRescheduleShortstay;

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    background: "#000",
    color: "#fff",
    minHeight: "100vh",
    padding: "80px 20px 140px",
    fontFamily: "sans-serif",
  },
  title: {
    fontSize: "1.8rem",
    textAlign: "center",
    marginBottom: 20,
  },
  card: {
    background: "#111",
    borderRadius: 16,
    padding: 24,
    maxWidth: 500,
    margin: "0 auto",
    border: "1px solid #bf9b30",
  },
  subTitle: {
    fontSize: "1.2rem",
    marginBottom: 10,
  },
  hr: {
    border: "none",
    borderTop: "1px solid #333",
    margin: "16px 0",
  },
  confirmBtn: {
    marginTop: 20,
    padding: "12px 20px",
    borderRadius: 30,
    border: "none",
    fontWeight: 600,
    fontSize: 16,
    background: "linear-gradient(to right, #fff, #bf9b30)",
    color: "#000",
    cursor: "pointer",
    width: "100%",
  },
  modal: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
  },
  modalContent: {
    background: "#222",
    padding: 24,
    borderRadius: 12,
    width: "90%",
    maxWidth: 400,
    textAlign: "center",
    border: "1px solid #bf9b30",
  },
  modalBtn: {
    marginTop: 16,
    padding: "10px 20px",
    borderRadius: 30,
    border: "none",
    fontWeight: 600,
    background: "linear-gradient(to right, #fff, #bf9b30)",
    color: "#000",
    cursor: "pointer",
    width: "100%",
  },
  cancelBtn: {
    marginTop: 10,
    padding: "10px 20px",
    borderRadius: 30,
    border: "1px solid #444",
    background: "#111",
    color: "#ccc",
    cursor: "pointer",
    width: "100%",
  },
};